package com.upcominds.expedite.features.rewards

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.ApiException
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.rewards.RewardsRepository
import com.upcominds.expedite.dto.RewardDTO
import com.upcominds.expedite.dto.RewardRedemptionDTO
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class RewardsUiState(
    val loading: Boolean = false,
    val redeemingRewardId: String? = null,
    val error: String? = null,
    val pointsBalance: Int = 0,
    val rewards: List<RewardDTO> = emptyList(),
    val redemptions: List<RewardRedemptionDTO> = emptyList(),
    val latestRedemption: RewardRedemptionDTO? = null
)

class RewardsViewModel(
    private val rewardsRepository: RewardsRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(RewardsUiState())
    val uiState: StateFlow<RewardsUiState> = _uiState.asStateFlow()

    fun load() {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val catalog = rewardsRepository.getCatalog()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    pointsBalance = catalog.pointsBalance,
                    rewards = catalog.rewards,
                    redemptions = catalog.redemptions
                )
            } catch (e: Exception) {
                logMobileError("RewardsViewModel.load", e)
                _uiState.value = _uiState.value.copy(loading = false, error = toUserMessage(e))
            }
        }
    }

    fun redeem(reward: RewardDTO) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(redeemingRewardId = reward.id, error = null, latestRedemption = null)
            try {
                val result = rewardsRepository.redeemReward(reward.id)
                val catalog = rewardsRepository.getCatalog()
                _uiState.value = _uiState.value.copy(
                    redeemingRewardId = null,
                    pointsBalance = result.pointsBalance,
                    rewards = catalog.rewards,
                    redemptions = catalog.redemptions,
                    latestRedemption = result.redemption
                )
            } catch (e: Exception) {
                logMobileError("RewardsViewModel.redeem", e, "Failed to redeem reward ${reward.id}")
                _uiState.value = _uiState.value.copy(redeemingRewardId = null, error = toUserMessage(e))
            }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    private fun toUserMessage(error: Exception): String {
        val apiError = error as? ApiException ?: return error.message ?: "Something went wrong. Please try again."
        return when (apiError.statusCode) {
            400 -> apiError.message.ifBlank { "This reward cannot be redeemed." }
            401 -> "Your session has expired. Please sign in again."
            409 -> apiError.message.ifBlank { "This reward is not available right now." }
            in 500..599 -> "Rewards are unavailable right now. Please try again in a bit."
            else -> apiError.message.ifBlank { "Reward action failed. Please try again." }
        }
    }
}
